import type {
  CountryCode,
  CreateKlarnaOrderPayload,
  CreateOrderRequest,
  CreateOrderResponse,
  KlarnaOrder,
  KlarnaOrderLine,
  Language,
  OrderDTO
} from 'ecosystem'
import {
  klarnaApiCall,
  klarnaOrderLineFactory,
  parseStoreToBackendOrderLines,
  paymentShippingFactory
} from 'api'
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, resolveUrlPath, sumArrayOfNumbers } from 'shared-utils'
import type { NextApiRequest, NextApiResponse } from 'next'
import storefrontApi from 'api/storefront-api'
import { type CreateOrderHandlerOpts } from './types'
import { ingridShippingProvider } from './ingridShippingProvider'
import { nshiftShippingProvider } from './nshiftShippingProvider'

/**
 * The country the Klarna session is created for: the shopper's pick when the
 * merchant offers a choice, otherwise the configured purchase country.
 *
 * `availableCountries` is the only gate. Which country/currency combinations
 * are actually permitted is set on the merchant's Kustom contract, not by a
 * fixed pairing — selling to Poland in SEK works when the account covers it,
 * it just means Kustom's Polish local payment methods are not offered. See
 * {@link KUSTOM_COUNTRY_CURRENCY} for each core country's local currency.
 * @see https://docs.kustom.co/contents/checkout/use-cases/selling-to-multiple-countries
 */
const resolveKlarnaCountry = (
  opts: CreateOrderHandlerOpts,
  requestedCountryCode: CountryCode | undefined
): string | undefined => {
  const { availableCountries, purchaseCountry } =
    opts.paymentProviderConfig?.providerOptions?.klarna ?? {}

  if (
    requestedCountryCode &&
    (!availableCountries?.length || availableCountries.includes(requestedCountryCode))
  ) {
    return requestedCountryCode
  }

  return purchaseCountry
}

const generateCreateKlarnaOrderPayload = ({
  opts,
  locale,
  cartDiscountCode,
  items,
  backendOrder,
  sessionCountryCode
}: Pick<CreateOrderRequest, 'cartDiscountCode' | 'items'> & {
  locale: string
  opts: CreateOrderHandlerOpts
  backendOrder: OrderDTO
  /** The shopper's country pick, already validated by `resolveKlarnaCountry`. */
  sessionCountryCode?: string
}) => {
  const { orderLines, orderAmount } = klarnaOrderLineFactory(
    items,
    cartDiscountCode,
    opts.paymentProviderConfig?.providerOptions?.klarna?.purchaseCountry,
    opts.buyerVat
  )
  const taxAmount: number = sumArrayOfNumbers(
    orderLines.map((item: KlarnaOrderLine) => item.total_tax_amount)
  )

  return {
    locale,
    purchase_country:
      sessionCountryCode ||
      opts.paymentProviderConfig?.providerOptions?.klarna?.purchaseCountry ||
      DEFAULT_COUNTRY,
    purchase_currency:
      opts.paymentProviderConfig?.providerOptions?.klarna?.purchaseCurrency || DEFAULT_CURRENCY,
    order_amount: orderAmount,
    order_tax_amount: taxAmount,
    order_lines: orderLines,
    merchant_reference1: backendOrder.orderNumber,
    merchant_reference2: '',
    merchant_data: '',
    merchant_urls: {
      terms: resolveUrlPath(
        opts.redirectDomain,
        opts.paymentProviderConfig?.providerOptions?.klarna?.termsUrl ?? '/terms'
      ),
      checkout: resolveUrlPath(opts.redirectDomain, '/checkout?order_id={checkout.order.id}'),
      confirmation: resolveUrlPath(
        opts.redirectDomain,
        '/confirmation?order_id={checkout.order.id}'
      ),
      push: resolveUrlPath(opts.redirectDomain, '/api/orders/push?order_id={checkout.order.id}'),
      ...(opts.paymentProviderConfig?.providerOptions?.klarna?.cancellationTermsUrl
        ? {
            cancellation_terms: resolveUrlPath(
              opts.redirectDomain,
              opts.paymentProviderConfig?.providerOptions?.klarna?.cancellationTermsUrl
            )
          }
        : {})
    },
    options: {
      phone_mandatory: opts.paymentProviderConfig?.providerOptions?.klarna?.phoneMandatory ?? true,
      allow_separate_shipping_address: false,
      color_button: opts.paymentProviderConfig?.providerOptions?.klarna?.colorButton || opts.color,
      color_checkbox: opts.color,
      color_checkbox_checkmark: opts.color,
      color_header: opts.color,
      color_link: opts.color,
      radius_border: 'sm'
    }
  } as CreateKlarnaOrderPayload
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials
  // VAT follows the merchant's configured country; the delivery rules follow
  // the country the session is actually created for.
  const countryCode = opts.paymentProviderConfig?.providerOptions?.klarna?.purchaseCountry
  const sessionCountryCode = resolveKlarnaCountry(opts, requestedCountryCode)

  const { orderAmount, netOrderAmount } = klarnaOrderLineFactory(
    items,
    cartDiscountCode,
    countryCode,
    opts.buyerVat
  )
  const shippingInfo = paymentShippingFactory({
    orderAmount,
    netOrderAmount,
    locale,
    opts: opts.shippingProviderConfig?.simpleShipping,
    shippingConfig: opts.shippingProviderConfig,
    customerType: opts.customerType,
    recipientCountryCode: sessionCountryCode,
    countryCode,
    buyerVat: opts.buyerVat
  })

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'KLARNA'
    }
  })

  const klarnaPayload = generateCreateKlarnaOrderPayload({
    sessionCountryCode,
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    locale
  })

  const basePayload: CreateKlarnaOrderPayload = {
    ...klarnaPayload,
    options: {
      ...klarnaPayload.options,
      allow_separate_shipping_address: true
    }
  }

  // Klarna adds the selected `shipping_options` entry on top of `order_lines`.
  // That is right for the legacy single flat rate, which nothing else ever
  // touches — but a store with configured delivery options has an FE delivery
  // step that pushes its own `shipping_fee` line on every change, so leaving a
  // shipping option in place would charge the shopper freight twice. There,
  // freight goes in the lines from the start and Klarna renders no selector of
  // its own.
  const hasConfiguredMethods = Boolean(opts.shippingProviderConfig?.defaultOptions?.methods?.length)

  const payload: CreateKlarnaOrderPayload = hasConfiguredMethods
    ? {
        ...basePayload,
        order_lines: [
          ...basePayload.order_lines,
          {
            type: 'shipping_fee',
            name: shippingInfo.name,
            quantity: 1,
            unit_price: shippingInfo.price,
            total_amount: shippingInfo.price,
            tax_rate: shippingInfo.tax_rate,
            total_tax_amount: shippingInfo.tax_amount,
            total_discount_amount: 0,
            merchant_data: ''
          }
        ],
        order_amount: basePayload.order_amount + shippingInfo.price,
        order_tax_amount: basePayload.order_tax_amount + shippingInfo.tax_amount
      }
    : { ...basePayload, shipping_options: [shippingInfo] }

  const { error, data } = await klarnaApiCall<KlarnaOrder, CreateKlarnaOrderPayload>('create', {
    payload
  })

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({ storefrontOrderResponse: backendOrder, klarnaResponse: data })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'KLARNA'
    },
    shipping: {
      provider: 'POSTNORD'
    }
  })

  const klarnaPayload = generateCreateKlarnaOrderPayload({
    sessionCountryCode: resolveKlarnaCountry(opts, requestedCountryCode),
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    locale
  })

  const { error, data } = await klarnaApiCall<KlarnaOrder, CreateKlarnaOrderPayload>('create', {
    payload: klarnaPayload
  })

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    klarnaResponse: data
  })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const {
      storeId,
      items,
      cartDiscountCode,
      countryCode: requestedCountryCode
    }: CreateOrderRequest = JSON.parse(req.body)
    const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    const locale = opts.locale || localeForCredentials

    const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const backendOrder = await storefrontApi.orders.createStorefrontOrder({
      storeId,
      backendOrderLines,
      cartDiscountCode,
      customerId: opts.customerId,
      paymentProviderInfo: {
        providerName: 'KLARNA'
      },
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    const klarnaPayload = generateCreateKlarnaOrderPayload({
      sessionCountryCode: resolveKlarnaCountry(opts, requestedCountryCode),
      opts,
      backendOrder,
      items,
      cartDiscountCode,
      locale
    })

    const { error, data } = await klarnaApiCall<KlarnaOrder, CreateKlarnaOrderPayload>('create', {
      payload: klarnaPayload
    })

    if (error || !data) {
      res.status(400).json({ error: error || 'Unknown error' })
      return
    }

    res.status(200).json({
      storefrontOrderResponse: backendOrder,
      klarnaResponse: data,
      ingridResponse
    })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)

  const {
    storeId,
    items,
    cartDiscountCode,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'
  const locale = opts.locale || localeForCredentials

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'KLARNA'
    },
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  const klarnaPayload = generateCreateKlarnaOrderPayload({
    sessionCountryCode: resolveKlarnaCountry(opts, requestedCountryCode),
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    locale
  })

  const { error, data } = await klarnaApiCall<KlarnaOrder, CreateKlarnaOrderPayload>('create', {
    payload: klarnaPayload
  })

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    klarnaResponse: data,
    nshiftResponse
  })
}

export const klarnaPaymentProvider = async (
  req: NextApiRequest,
  res: NextApiResponse,
  opts: CreateOrderHandlerOpts
) => {
  const { shippingProviderConfig } = opts

  switch (shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'INGRID':
      return ingridShipping(req, res, opts)
    case 'POSTNORD':
      return postnordShipping(req, res, opts)
    case 'NSHIFT':
      return nshiftShipping(req, res, opts)
    case 'DEFAULT':
      return simpleShipping(req, res, opts)
  }
}
